import React, { useState, useEffect, useCallback } from "react";
import { ArrowLeft, ArrowRight, Plus, Eye, CheckCircle2, Clock, Loader2, FileText, Lightbulb, Check, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  CLINIC_COLORS, BTN_GRADIENT, CLINICAL_CARD,
  THYROID_TESTS, INFLAMMATION_TESTS, BLOOD_PANEL_TESTS,
  INITIAL_QUESTIONNAIRE,
} from "./constants";

const API = process.env.REACT_APP_BACKEND_URL;

const CARD_STYLE = {
  background: "#fff", borderRadius: "0.75rem",
  boxShadow: "0 4px 20px rgba(0,101,141,0.06)",
  border: "1px solid rgba(189,200,209,0.15)",
};

const FILLED = {
  background: "#f1f4f9", border: "2px solid transparent",
  transition: "border-color 0.2s, background-color 0.2s",
};

const onFocus = (e) => { e.target.style.borderColor = CLINIC_COLORS.primary; e.target.style.background = "#fff"; };
const onBlur = (e) => { e.target.style.borderColor = "transparent"; e.target.style.background = "#f1f4f9"; };

function YesNoRadio({ id, label, value, onChange }) {
  const base = "px-6 py-3 rounded-full border-2 font-semibold text-sm transition-all cursor-pointer select-none";
  const sel = { borderColor: "#00658D", background: "rgba(198,231,255,0.4)", color: "#00658D" };
  const un = { borderColor: "#dfe3e8", background: "transparent", color: "#3e4850" };
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-2">
      <Label htmlFor={`${id}-yes`} className="font-medium text-base" style={{ color: "#181c20" }}>{label}</Label>
      <div className="flex gap-3 shrink-0">
        <button type="button" onClick={() => onChange("yes")} className={base} style={value === "yes" ? sel : un}
          onMouseEnter={e => { if (value !== "yes") e.currentTarget.style.background = "#f1f4f9"; }}
          onMouseLeave={e => { if (value !== "yes") e.currentTarget.style.background = "transparent"; }}
          data-testid={`${id}-yes`}>Jah</button>
        <button type="button" onClick={() => onChange("no")} className={base} style={value === "no" ? sel : un}
          onMouseEnter={e => { if (value !== "no") e.currentTarget.style.background = "#f1f4f9"; }}
          onMouseLeave={e => { if (value !== "no") e.currentTarget.style.background = "transparent"; }}
          data-testid={`${id}-no`}>Ei</button>
      </div>
    </div>
  );
}

function StepProgress({ currentStep, total, label }) {
  const pct = Math.round((currentStep / total) * 100);
  return (
    <div className="mb-10">
      <div className="flex justify-between items-end mb-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.15em]" style={{ color: "#00658D" }} data-testid="step-progress-indicator">
            Samm {currentStep} / {total}
          </span>
          <h1 className="text-3xl font-bold tracking-tight mt-1" style={{ color: "#181c20" }}>{label}</h1>
        </div>
        <span className="text-sm font-medium hidden sm:block" style={{ color: "#3e4850" }}>Lõpetatud: {pct}%</span>
      </div>
      <div className="h-2 w-full rounded-full overflow-hidden" style={{ background: "#ebeef3" }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #00aeef, #00658d)", boxShadow: "0 0 8px rgba(0,174,239,0.4)" }} />
      </div>
    </div>
  );
}

export default function ThyroidNurseView({ token, userName }) {
  const [view, setView] = useState("list"); // list | form | detail
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState(1);
  const [complaints, setComplaints] = useState("");
  const [questionnaire, setQuestionnaire] = useState({ ...INITIAL_QUESTIONNAIRE });
  const [testValues, setTestValues] = useState({});
  const [ultrasoundText, setUltrasoundText] = useState("");
  const [patientFirstName, setPatientFirstName] = useState("");
  const [patientLastName, setPatientLastName] = useState("");
  const [patientIdCode, setPatientIdCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const STEPS = [
    { num: 1, label: "Sümptomid" },
    { num: 2, label: "Küsimustik" },
    { num: 3, label: "Laboritulemused" },
    { num: 4, label: "Ultraheli" },
    { num: 5, label: "Patsiendi andmed" },
  ];

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/thyroid/nurse/submissions`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setItems(data.items || []);
    } catch (_e) { /* ignore */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { if (view === "list") fetchItems(); }, [view, fetchItems]);

  const updateQ = (key, value) => {
    if (key === "thiamazole" && value === "yes") {
      setQuestionnaire(prev => ({ ...prev, [key]: value, propylthiouracil: "no", propylthiouracilDosage: "" }));
    } else if (key === "propylthiouracil" && value === "yes") {
      setQuestionnaire(prev => ({ ...prev, [key]: value, thiamazole: "no", thiamazoleDosage: "" }));
    } else {
      setQuestionnaire(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleInputChange = (key, value) => {
    const filtered = value.replace(/[^0-9.,]/g, '').replace(',', '.');
    if (filtered === "" || filtered === "." || /^[0-9]*\.?[0-9]*$/.test(filtered)) {
      setTestValues(prev => ({ ...prev, [key]: filtered }));
    }
  };

  const hasAcuteSymptoms = questionnaire.hasFever === "yes" || questionnaire.hasFatigue === "yes" || questionnaire.hasNeckPain === "yes" || questionnaire.symptomsAfterVirusInfection === "yes";
  const isBloodPanelRequired = questionnaire.thiamazole === "yes" || questionnaire.propylthiouracil === "yes";
  const hasAnyValue = Object.values(testValues).some(v => v !== "" && v !== undefined);

  const isQuestionnaireComplete = () => {
    const required = ['age', 'gender', 'thyroidDiseasesBefore', 'levothyroxin', 'thiamazole', 'propylthiouracil', 'cordarone', 'iodinatedSalt', 'iodineSkin', 'iodineSpray', 'ctContrast'];
    if (!required.every(k => questionnaire[k] !== "")) return false;
    const age = parseInt(questionnaire.age);
    if (questionnaire.gender === "female" && age >= 15 && age <= 48 && questionnaire.pregnant === "") return false;
    if (questionnaire.thyroidDiseasesBefore === "yes") {
      if (!["graves", "hashimoto", "thyroidNodules", "thyroidCancer", "totalThyroidectomy", "partialThyroidectomy"].every(k => questionnaire[k] !== "")) return false;
    }
    if (questionnaire.levothyroxin === "yes") {
      if (questionnaire.levoBeforeBreakfast === "" || questionnaire.levoRegularly === "" || questionnaire.takesCalcium === "" || questionnaire.takesIron === "" || questionnaire.takesAcidReducer === "") return false;
      if ((questionnaire.takesCalcium === "yes" || questionnaire.takesIron === "yes" || questionnaire.takesAcidReducer === "yes") && questionnaire.levoTwoHoursApart === "") return false;
    }
    return true;
  };

  const isBloodPanelComplete = () => {
    if (!isBloodPanelRequired) return true;
    return BLOOD_PANEL_TESTS.every(t => testValues[t.key] && testValues[t.key] !== "");
  };

  const parseTests = () => {
    const tests = {};
    const parse = (group) => group.forEach(t => {
      const v = testValues[t.key];
      const n = v ? v.toString().replace(',', '.') : v;
      tests[t.key] = n !== "" && n !== undefined ? parseFloat(n) : null;
    });
    parse(THYROID_TESTS);
    parse(BLOOD_PANEL_TESTS);
    if (hasAcuteSymptoms) parse(INFLAMMATION_TESTS);
    return tests;
  };

  const handleSubmit = async () => {
    setError("");
    if (!patientFirstName.trim() || !patientLastName.trim()) { setError("Patsiendi nimi on kohustuslik"); return; }
    if (!patientIdCode.trim()) { setError("Isikukood on kohustuslik"); return; }

    setSubmitting(true);
    try {
      const body = {
        complaints, questionnaire, tests: parseTests(),
        ultrasound_text: ultrasoundText,
        patient_first_name: patientFirstName.trim(),
        patient_last_name: patientLastName.trim(),
        patient_id_code: patientIdCode.trim(),
      };
      const res = await fetch(`${API}/api/thyroid/diagnoses`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Viga"); }
      resetForm();
      setView("list");
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setStep(1); setComplaints(""); setQuestionnaire({ ...INITIAL_QUESTIONNAIRE });
    setTestValues({}); setUltrasoundText("");
    setPatientFirstName(""); setPatientLastName(""); setPatientIdCode("");
    setError("");
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("et-EE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const authFetch = useCallback((url, opts = {}) => {
    return fetch(url, { ...opts, headers: { ...opts.headers, Authorization: `Bearer ${token}` } });
  }, [token]);

  const handlePdf = async (action) => {
    if (!selected) return;
    try {
      toast.info(action === "print" ? "Genereerin..." : "Laen alla...");
      const res = await authFetch(`${API}/api/thyroid/diagnoses/${selected.diagnosis_id}/pdf`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (action === "print") {
        const w = window.open(url);
        if (w) w.onload = () => w.print();
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `Kilpnaare_raport_${selected.diagnosis_id.slice(-6)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error("PDF genereerimine ebaõnnestus");
    }
  };

  // ====== DETAIL VIEW ======
  if (view === "detail" && selected) {
    return (
      <div data-testid="nurse-detail-view" className="max-w-4xl mx-auto">
        <Button variant="outline" onClick={() => { setSelected(null); setView("list"); }} className="rounded-full mb-6" data-testid="nurse-detail-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />Tagasi
        </Button>
        <div className="rounded-3xl p-8 mb-6" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-2xl font-bold" style={{ color: "#181c20" }}>Analüüsi üksikasjad</h2>
            {selected.doctor_approved_at ? (
              <Badge style={{ background: "rgba(16,185,129,0.12)", color: "#059669", border: "none" }}>
                <CheckCircle2 className="w-3 h-3 mr-1" />Kinnitas: {selected.doctor_approved_by_name} · {formatDate(selected.doctor_approved_at)}
              </Badge>
            ) : (
              <Badge style={{ background: "rgba(251,191,36,0.15)", color: "#92400e", border: "none" }}>
                <Clock className="w-3 h-3 mr-1" />Ootab arsti kinnitust
              </Badge>
            )}
          </div>
          <p className="text-sm mb-4" style={{ color: "#6e7881" }}>
            Esitatud: {formatDate(selected.created_at)}
            {selected.patient_first_name && ` · Patsient: ${selected.patient_first_name} ${selected.patient_last_name}`}
          </p>
          {selected.doctor_approved_at && selected.result ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold" style={{ color: "#00658D" }}>Lõplik raport</h3>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handlePdf("print")} data-testid="thyroid-nurse-print-btn" title="Prindi">
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handlePdf("download")} data-testid="thyroid-nurse-download-btn" title="Lae alla">
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="whitespace-pre-wrap p-6 rounded-xl text-sm" style={{ background: "#f7f9fe", color: "#181c20" }} data-testid="nurse-final-report">{selected.result}</div>
            </>
          ) : (
            <div className="p-6 rounded-xl text-center" style={{ background: "#fff7ed", color: "#92400e" }}>
              <Clock className="w-8 h-8 mx-auto mb-3" />
              <p className="font-semibold mb-1">Ootab arsti kinnitust</p>
              <p className="text-sm">AI mustand kuvatakse ainult pärast seda, kui arst on selle üle vaadanud ja kinnitanud.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ====== FORM VIEW ======
  if (view === "form") {
    return (
      <div className="max-w-4xl mx-auto" data-testid="nurse-form-view">
        <Button variant="outline" onClick={() => setView("list")} className="rounded-full mb-6" data-testid="nurse-form-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />Tagasi
        </Button>
        <StepProgress currentStep={step} total={5} label={STEPS[step - 1]?.label} />

        {/* STEP 1: Symptoms */}
        {step === 1 && (
          <div className="space-y-6 animate-fade-in">
            <div className="p-8" style={CARD_STYLE}>
              <label className="font-medium text-sm block mb-3" style={{ color: CLINIC_COLORS.muted }}>Patsiendi sümptomid või kaebused</label>
              <textarea data-testid="input-complaints" className="w-full min-h-[160px] p-4 rounded-xl resize-y text-base focus:outline-none" style={FILLED}
                onFocus={onFocus} onBlur={onBlur} placeholder="Kirjeldage sümptomeid (nt väsimus, kehakaalu muutused, südamepekslemine...)" value={complaints} onChange={e => setComplaints(e.target.value)} />
              <p className="text-sm italic mt-2" style={{ color: CLINIC_COLORS.subtle }}>See väli on valikuline.</p>
            </div>
            <div className="p-8 space-y-4" style={CARD_STYLE}>
              <h3 className="font-semibold" style={{ color: CLINIC_COLORS.ink }}>Ägedad sümptomid</h3>
              <YesNoRadio id="hasFever" label="Kas patsiendil on palavik?" value={questionnaire.hasFever} onChange={v => updateQ("hasFever", v)} />
              <YesNoRadio id="hasFatigue" label="Kas tunneb väsimust või nõrkust?" value={questionnaire.hasFatigue} onChange={v => updateQ("hasFatigue", v)} />
              <YesNoRadio id="hasNeckPain" label="Kas on valu kaela esiosas?" value={questionnaire.hasNeckPain} onChange={v => updateQ("hasNeckPain", v)} />
              <YesNoRadio id="symptomsAfterVirusInfection" label="Kas sümptomid algasid pärast viirusinfektsiooni?" value={questionnaire.symptomsAfterVirusInfection} onChange={v => updateQ("symptomsAfterVirusInfection", v)} />
              {hasAcuteSymptoms && (
                <div className="pt-4 space-y-4">
                  <h3 className="font-semibold" style={{ color: CLINIC_COLORS.ink }}>Põletikumarkerid</h3>
                  {INFLAMMATION_TESTS.map(t => (
                    <div key={t.key} className="flex items-center gap-4">
                      <Label className="w-28 font-semibold" style={{ color: CLINIC_COLORS.muted }}>{t.name}</Label>
                      <div className="flex-1 relative">
                        <Input data-testid={`input-${t.key}`} type="text" inputMode="decimal" placeholder="Soovituslik" className="pr-20 h-12"
                          style={FILLED} onFocus={onFocus} onBlur={onBlur} value={testValues[t.key] || ""} onChange={e => handleInputChange(t.key, e.target.value)} />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium" style={{ color: CLINIC_COLORS.subtle }}>{t.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end pt-4">
              <button type="button" data-testid="continue-to-questionnaire-btn" onClick={() => setStep(2)}
                className="px-12 py-4 text-white font-bold rounded-full shadow-lg transition-all hover:scale-[1.02] inline-flex items-center gap-2"
                style={{ background: BTN_GRADIENT, boxShadow: "0 8px 20px rgba(0,101,141,0.25)" }}>
                Edasi <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Questionnaire */}
        {step === 2 && (
          <div className="space-y-6 animate-fade-in">
            <div className="p-6 md:p-8" style={CARD_STYLE}>
              <h2 className="text-xl font-semibold mb-4" style={{ color: CLINIC_COLORS.primary }}>Põhiteave</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>Vanus</Label>
                  <Input data-testid="input-age" type="number" min="1" max="120" placeholder="nt 34" className="h-12 rounded-lg"
                    style={FILLED} onFocus={onFocus} onBlur={onBlur} value={questionnaire.age} onChange={e => updateQ("age", e.target.value.replace(/[^0-9]/g, ""))} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>Sugu</Label>
                  <div className="flex gap-3 flex-wrap" data-testid="gender-toggle">
                    {[{ v: "male", l: "Mees" }, { v: "female", l: "Naine" }, { v: "other", l: "Muu" }].map(o => (
                      <button key={o.v} type="button" data-testid={`gender-${o.v}`} onClick={() => updateQ("gender", o.v)}
                        className="px-6 py-3 rounded-full border-2 font-semibold text-sm transition-all cursor-pointer"
                        style={questionnaire.gender === o.v ? { borderColor: "#00658D", background: "rgba(198,231,255,0.4)", color: "#00658D" } : { borderColor: "#dfe3e8", color: "#3e4850" }}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {questionnaire.gender === "female" && parseInt(questionnaire.age) >= 15 && parseInt(questionnaire.age) <= 48 && (
              <div className="p-6" style={CARD_STYLE}>
                <YesNoRadio id="pregnant" label="Kas patsient on rase?" value={questionnaire.pregnant} onChange={v => updateQ("pregnant", v)} />
                {questionnaire.pregnant === "yes" && (
                  <div className="mt-4 pl-4 border-l-2 space-y-2" style={{ borderColor: CLINIC_COLORS.primary }}>
                    <Label className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>Mitu nädalat?</Label>
                    <Input data-testid="input-pregnant-weeks" type="text" placeholder="nt 12, 20, 32" className="h-12 w-40 rounded-lg"
                      style={FILLED} onFocus={onFocus} onBlur={onBlur} value={questionnaire.pregnantWeeks} onChange={e => updateQ("pregnantWeeks", e.target.value)} />
                  </div>
                )}
              </div>
            )}
            <h2 className="text-xl font-semibold px-2 mt-4" style={{ color: CLINIC_COLORS.primary }}>Terviselugu</h2>
            <div className="p-6" style={CARD_STYLE}>
              <YesNoRadio id="thyroidDiseases" label="Kas varem on diagnoositud kilpnäärmehaigusi?" value={questionnaire.thyroidDiseasesBefore} onChange={v => updateQ("thyroidDiseasesBefore", v)} />
              {questionnaire.thyroidDiseasesBefore === "yes" && (
                <div className="mt-4 pl-4 border-l-2 space-y-3" style={{ borderColor: CLINIC_COLORS.primary }}>
                  <YesNoRadio id="graves" label="Gravesi tõbi" value={questionnaire.graves} onChange={v => updateQ("graves", v)} />
                  <YesNoRadio id="hashimoto" label="Hashimoto türeoidiit" value={questionnaire.hashimoto} onChange={v => updateQ("hashimoto", v)} />
                  <YesNoRadio id="thyroidNodules" label="Kilpnäärme sõlmed" value={questionnaire.thyroidNodules} onChange={v => updateQ("thyroidNodules", v)} />
                  <YesNoRadio id="thyroidCancer" label="Kilpnäärmevähk" value={questionnaire.thyroidCancer} onChange={v => updateQ("thyroidCancer", v)} />
                  <YesNoRadio id="totalThyroidectomy" label="Kilpnäärme täielik eemaldamine" value={questionnaire.totalThyroidectomy} onChange={v => updateQ("totalThyroidectomy", v)} />
                  <YesNoRadio id="partialThyroidectomy" label="Kilpnäärme osaline eemaldamine" value={questionnaire.partialThyroidectomy} onChange={v => updateQ("partialThyroidectomy", v)} />
                </div>
              )}
            </div>
            <h2 className="text-xl font-semibold px-2 mt-4" style={{ color: CLINIC_COLORS.primary }}>Regulaarsed ravimid</h2>
            <div className="p-6" style={CARD_STYLE}>
              <YesNoRadio id="levothyroxin" label="Levotüroksiin" value={questionnaire.levothyroxin} onChange={v => updateQ("levothyroxin", v)} />
              {questionnaire.levothyroxin === "yes" && (
                <div className="mt-4 pl-4 border-l-2 space-y-3" style={{ borderColor: CLINIC_COLORS.primary }}>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>Annus (mcg)</Label>
                    <Input data-testid="input-levothyroxin-dosage" type="text" placeholder="nt 50, 75, 100" className="h-12 w-40 rounded-lg"
                      style={FILLED} onFocus={onFocus} onBlur={onBlur} value={questionnaire.levothyroxinDosage} onChange={e => updateQ("levothyroxinDosage", e.target.value)} />
                  </div>
                  <YesNoRadio id="levoBeforeBreakfast" label="Võtab enne hommikusööki?" value={questionnaire.levoBeforeBreakfast} onChange={v => updateQ("levoBeforeBreakfast", v)} />
                  <YesNoRadio id="levoRegularly" label="Võtab regulaarselt?" value={questionnaire.levoRegularly} onChange={v => updateQ("levoRegularly", v)} />
                  <p className="text-sm font-medium pt-2" style={{ color: CLINIC_COLORS.muted }}>Kas võtab järgmisi ravimeid?</p>
                  <YesNoRadio id="takesCalcium" label="Kaltsiumilisandid" value={questionnaire.takesCalcium} onChange={v => updateQ("takesCalcium", v)} />
                  <YesNoRadio id="takesIron" label="Rauatablettid" value={questionnaire.takesIron} onChange={v => updateQ("takesIron", v)} />
                  <YesNoRadio id="takesAcidReducer" label="Maohapet vähendavad ravimid" value={questionnaire.takesAcidReducer} onChange={v => updateQ("takesAcidReducer", v)} />
                  {(questionnaire.takesCalcium === "yes" || questionnaire.takesIron === "yes" || questionnaire.takesAcidReducer === "yes") && (
                    <div className="mt-2 p-4 rounded-lg" style={{ background: "rgba(141,79,0,0.08)", border: "1px solid rgba(141,79,0,0.2)" }}>
                      <YesNoRadio id="levoTwoHoursApart" label="Kas võtab neid vähemalt 2h pärast levotüroksiini?" value={questionnaire.levoTwoHoursApart} onChange={v => updateQ("levoTwoHoursApart", v)} />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-6" style={CARD_STYLE}>
              <YesNoRadio id="thiamazole" label="Tiamasool" value={questionnaire.thiamazole} onChange={v => updateQ("thiamazole", v)} />
              {questionnaire.thiamazole === "yes" && (
                <div className="mt-4 pl-4 border-l-2 space-y-2" style={{ borderColor: CLINIC_COLORS.primary }}>
                  <Label className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>Annus (mg) päevas</Label>
                  <Input data-testid="input-thiamazole-dosage" type="text" placeholder="nt 5, 10, 20" className="h-12 w-40 rounded-lg"
                    style={FILLED} onFocus={onFocus} onBlur={onBlur} value={questionnaire.thiamazoleDosage} onChange={e => updateQ("thiamazoleDosage", e.target.value)} />
                </div>
              )}
            </div>
            <div className="p-6" style={CARD_STYLE}>
              <YesNoRadio id="propylthiouracil" label="Propüültiouratsiil" value={questionnaire.propylthiouracil} onChange={v => updateQ("propylthiouracil", v)} />
              {questionnaire.propylthiouracil === "yes" && (
                <div className="mt-4 pl-4 border-l-2 space-y-2" style={{ borderColor: CLINIC_COLORS.primary }}>
                  <Label className="text-sm font-medium" style={{ color: CLINIC_COLORS.muted }}>Annus (mg) päevas</Label>
                  <Input data-testid="input-propylthiouracil-dosage" type="text" placeholder="nt 50, 100" className="h-12 w-40 rounded-lg"
                    style={FILLED} onFocus={onFocus} onBlur={onBlur} value={questionnaire.propylthiouracilDosage} onChange={e => updateQ("propylthiouracilDosage", e.target.value)} />
                </div>
              )}
            </div>
            <div className="p-6" style={CARD_STYLE}><YesNoRadio id="cordarone" label="Cordarone / Amiodaroon" value={questionnaire.cordarone} onChange={v => updateQ("cordarone", v)} /></div>
            <div className="p-6" style={CARD_STYLE}><YesNoRadio id="heartDisease" label="Südamehaigus" value={questionnaire.heartDisease} onChange={v => updateQ("heartDisease", v)} /></div>
            <h2 className="text-xl font-semibold px-2 mt-4" style={{ color: CLINIC_COLORS.primary }}>Joodilisandid ja kokkupuude</h2>
            <div className="p-6" style={CARD_STYLE}><YesNoRadio id="iodinatedSalt" label="Jodeeritud sool" value={questionnaire.iodinatedSalt} onChange={v => updateQ("iodinatedSalt", v)} /></div>
            <div className="p-6" style={CARD_STYLE}><YesNoRadio id="iodineSkin" label="Joodilahused nahale" value={questionnaire.iodineSkin} onChange={v => updateQ("iodineSkin", v)} /></div>
            <div className="p-6" style={CARD_STYLE}><YesNoRadio id="iodineSpray" label="Joodiga suasprei" value={questionnaire.iodineSpray} onChange={v => updateQ("iodineSpray", v)} /></div>
            <h2 className="text-xl font-semibold px-2 mt-4" style={{ color: CLINIC_COLORS.primary }}>Hiljutine kompuutertomograafia</h2>
            <div className="p-6" style={CARD_STYLE}><YesNoRadio id="ctContrast" label="KT-uuring kontrastainega viimase 6 kuu jooksul" value={questionnaire.ctContrast} onChange={v => updateQ("ctContrast", v)} /></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-10">
              <button type="button" onClick={() => setStep(1)} className="px-8 py-3 font-semibold rounded-full inline-flex items-center gap-2" style={{ color: CLINIC_COLORS.primary }}
                data-testid="questionnaire-back-btn"><ArrowLeft className="w-4 h-4" />Tagasi</button>
              <button type="button" data-testid="continue-to-labs-btn" onClick={() => setStep(3)} disabled={!isQuestionnaireComplete()}
                className="px-12 py-4 text-white font-bold rounded-full shadow-lg transition-all hover:scale-[1.02] inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: BTN_GRADIENT, boxShadow: "0 8px 20px rgba(0,101,141,0.25)" }}>Edasi analüüse lisama <ArrowRight className="w-5 h-5" /></button>
            </div>
          </div>
        )}

        {/* STEP 3: Lab Results */}
        {step === 3 && (
          <div className="space-y-6 animate-fade-in">
            <div className="p-8" style={CARD_STYLE}>
              <h2 className="text-lg font-semibold mb-2" style={{ color: CLINIC_COLORS.ink }}>Kilpnäärme paneeli sisend</h2>
              <p className="text-sm mb-6" style={{ color: CLINIC_COLORS.subtle }}>Sisestage numbrilised väärtused laboriaruandest.</p>
              <div className="space-y-4">
                {THYROID_TESTS.map(t => (
                  <div key={t.key} className="flex items-center gap-4">
                    <Label className="w-28 font-semibold" style={{ color: CLINIC_COLORS.muted }}>{t.name}</Label>
                    <div className="flex-1 relative">
                      <Input data-testid={`input-${t.key}`} type="text" inputMode="decimal" placeholder={t.key === "tsh" ? "nt 0.005" : ""} className="pr-20 h-12 text-base"
                        style={FILLED} onFocus={onFocus} onBlur={onBlur} value={testValues[t.key] || ""} onChange={e => handleInputChange(t.key, e.target.value)} />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium" style={{ color: CLINIC_COLORS.subtle }}>{t.unit}</span>
                    </div>
                  </div>
                ))}
                {isBloodPanelRequired && (
                  <div className="mt-8 pt-6">
                    <h3 className="font-semibold flex items-center gap-2" style={{ color: CLINIC_COLORS.ink }}>
                      <span className="w-2 h-2 rounded-full" style={{ background: CLINIC_COLORS.warmAmber }} />Kliiniline vereanalüüs (kohustuslik)
                    </h3>
                    <p className="text-sm mt-1 mb-4" style={{ color: CLINIC_COLORS.subtle }}>Kohustuslik tiamasooli/propüültiouratsiili patsientidele.</p>
                    {BLOOD_PANEL_TESTS.map(t => (
                      <div key={t.key} className="flex items-center gap-4 mb-4">
                        <Label className="w-28 font-semibold" style={{ color: CLINIC_COLORS.muted }}>{t.name}</Label>
                        <div className="flex-1 relative">
                          <Input data-testid={`input-${t.key}`} type="text" inputMode="decimal" placeholder="Kohustuslik" className="pr-20 h-12 text-base"
                            style={FILLED} onFocus={onFocus} onBlur={onBlur} value={testValues[t.key] || ""} onChange={e => handleInputChange(t.key, e.target.value)} />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium" style={{ color: CLINIC_COLORS.subtle }}>{t.unit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4">
              <button type="button" onClick={() => setStep(2)} className="px-8 py-3 font-semibold rounded-full inline-flex items-center gap-2" style={{ color: CLINIC_COLORS.primary }}
                data-testid="labs-back-btn"><ArrowLeft className="w-4 h-4" />Tagasi</button>
              <button type="button" data-testid="continue-to-ultrasound-btn" onClick={() => setStep(4)} disabled={!hasAnyValue || !isBloodPanelComplete()}
                className="px-12 py-4 text-white font-bold rounded-full shadow-lg transition-all hover:scale-[1.02] inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: BTN_GRADIENT, boxShadow: "0 8px 20px rgba(0,101,141,0.25)" }}>Jätka <ArrowRight className="w-5 h-5" /></button>
            </div>
          </div>
        )}

        {/* STEP 4: Ultrasound */}
        {step === 4 && (
          <div className="space-y-6 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="p-8" style={CARD_STYLE} data-testid="ultrasound-card">
                  <div className="flex items-center gap-3 mb-6">
                    <FileText className="w-6 h-6" style={{ color: CLINIC_COLORS.primary }} />
                    <h2 className="text-lg font-semibold" style={{ color: "#181c20" }}>Ultraheli vastuse tekst</h2>
                  </div>
                  <textarea data-testid="input-ultrasound-text" className="w-full min-h-[360px] p-6 rounded-xl text-base leading-relaxed resize-none focus:outline-none"
                    style={FILLED} onFocus={onFocus} onBlur={onBlur} placeholder="Kleepige ultraheli vastuse tekst siia..." value={ultrasoundText} onChange={e => setUltrasoundText(e.target.value)} />
                </div>
              </div>
              <aside className="space-y-6">
                <div className="p-6 rounded-xl" style={{ background: "rgba(198,231,255,0.25)", border: "1px solid rgba(0,101,141,0.12)" }}>
                  <div className="flex items-center gap-3 mb-3" style={{ color: CLINIC_COLORS.primary }}>
                    <Lightbulb className="w-5 h-5" /><h3 className="font-bold">Kuidas see toimib?</h3>
                  </div>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: CLINIC_COLORS.muted }}>Meie süsteem analüüsib ultraheli raportit, et tuvastada olulisi markereid.</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-xs" style={{ color: CLINIC_COLORS.muted }}><Check className="w-4 h-4 shrink-0" style={{ color: CLINIC_COLORS.primary }} />Kopeerige kogu tekst digiloost.</li>
                    <li className="flex items-start gap-2 text-xs" style={{ color: CLINIC_COLORS.muted }}><Check className="w-4 h-4 shrink-0" style={{ color: CLINIC_COLORS.primary }} />Veenduge, et kaasatud on mõõtmed.</li>
                  </ul>
                </div>
              </aside>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4">
              <button type="button" onClick={() => setStep(3)} className="px-8 py-3 font-semibold rounded-full inline-flex items-center gap-2" style={{ color: CLINIC_COLORS.primary }}
                data-testid="ultrasound-back-btn"><ArrowLeft className="w-4 h-4" />Tagasi</button>
              <div className="flex gap-3">
                <button type="button" data-testid="skip-ultrasound-btn" onClick={() => setStep(5)}
                  className="px-8 py-3 font-semibold rounded-full" style={{ color: CLINIC_COLORS.muted }}>Jäta vahele</button>
                <button type="button" data-testid="continue-ultrasound-btn" onClick={() => setStep(5)} disabled={!ultrasoundText?.trim()}
                  className="px-12 py-4 text-white font-bold rounded-full shadow-lg transition-all hover:scale-[1.02] inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: BTN_GRADIENT, boxShadow: "0 8px 20px rgba(0,101,141,0.25)" }}>Jätka <ArrowRight className="w-5 h-5" /></button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 5: Patient info & Submit */}
        {step === 5 && (
          <div className="space-y-6 animate-fade-in">
            <div className="rounded-3xl p-8" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)", border: "1px solid rgba(189,200,209,0.15)" }} data-testid="submit-card">
              <h3 className="text-lg font-semibold mb-3" style={{ color: "#181c20" }}>Patsiendi andmed ja esitamine</h3>
              <p className="text-sm mb-6" style={{ color: "#6e7881" }}>AI koostab mustandi taustal. Te ei näe mustandit enne, kui arst on selle kinnitanud.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold" style={{ color: "#3e4850" }}>
                    Eesnimi <span className="text-red-500">*</span>
                  </Label>
                  <Input data-testid="input-patient-first-name" type="text" placeholder="Patsiendi eesnimi" className="h-12 rounded-xl"
                    style={FILLED} onFocus={onFocus} onBlur={onBlur} value={patientFirstName} onChange={e => setPatientFirstName(e.target.value.slice(0, 100))} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold" style={{ color: "#3e4850" }}>
                    Perekonnanimi <span className="text-red-500">*</span>
                  </Label>
                  <Input data-testid="input-patient-last-name" type="text" placeholder="Patsiendi perekonnanimi" className="h-12 rounded-xl"
                    style={FILLED} onFocus={onFocus} onBlur={onBlur} value={patientLastName} onChange={e => setPatientLastName(e.target.value.slice(0, 100))} />
                </div>
              </div>
              <div className="mb-6 space-y-2">
                <Label className="text-sm font-semibold" style={{ color: "#3e4850" }}>
                  Isikukood <span className="text-red-500">*</span>
                </Label>
                <Input data-testid="input-patient-id-code" type="text" placeholder="Patsiendi isikukood" className="h-12 w-full md:w-64 rounded-xl"
                  style={FILLED} onFocus={onFocus} onBlur={onBlur} value={patientIdCode} onChange={e => setPatientIdCode(e.target.value.slice(0, 20))} />
              </div>
              {error && <div className="text-sm p-3 rounded-xl mb-4" style={{ background: "rgba(141,79,0,0.08)", color: "#6d3a00" }} data-testid="submit-error">{error}</div>}
              <Button data-testid="nurse-submit-btn" onClick={handleSubmit}
                className="w-full text-white h-14 text-lg rounded-full inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: BTN_GRADIENT, boxShadow: "0 4px 16px rgba(0,101,141,0.25)" }}
                disabled={submitting || !patientFirstName.trim() || !patientLastName.trim() || !patientIdCode.trim()}>
                {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" />Töötlen...</>) : (<>Esita arstile kinnitamiseks <ArrowRight className="ml-2 w-5 h-5" /></>)}
              </Button>
            </div>
            <button type="button" onClick={() => setStep(4)} className="px-8 py-3 font-semibold rounded-full inline-flex items-center gap-2" style={{ color: "#00658D" }}
              data-testid="submit-back-btn"><ArrowLeft className="w-4 h-4" />Tagasi</button>
          </div>
        )}
      </div>
    );
  }

  // ====== LIST VIEW ======
  return (
    <div className="max-w-4xl mx-auto" data-testid="nurse-list-view">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: "#181c20" }}>Minu esitatud analüüsid</h2>
          <p className="text-sm mt-1" style={{ color: "#6e7881" }}>AI mustand on peidetud kuni arst on selle kinnitanud.</p>
        </div>
        <Button data-testid="new-submission-btn" onClick={() => { resetForm(); setView("form"); }}
          className="rounded-full text-white" style={{ background: BTN_GRADIENT }}>
          <Plus className="w-4 h-4 mr-2" />Uus analüüs
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#00658D" }} /></div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl p-12 text-center" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
          <p style={{ color: "#6e7881" }}>Teil ei ole veel esitatud analüüse.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(it => (
            <div key={it.diagnosis_id} className="rounded-2xl p-5 flex items-center justify-between hover:shadow-md transition-shadow cursor-pointer"
              style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)", border: "1px solid rgba(189,200,209,0.15)" }}
              onClick={() => { setSelected(it); setView("detail"); }} data-testid={`nurse-case-${it.diagnosis_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <span className="font-semibold" style={{ color: "#181c20" }}>
                    {it.patient_first_name && <span style={{ color: "#00658D" }}>{it.patient_first_name} {it.patient_last_name} · </span>}
                    Patsient: {it.patient_age ? `${it.patient_age}a` : "-"}{it.patient_gender ? `, ${it.patient_gender}` : ""}
                  </span>
                  {it.doctor_approved_at ? (
                    <Badge style={{ background: "rgba(16,185,129,0.12)", color: "#059669", border: "none" }}>
                      <CheckCircle2 className="w-3 h-3 mr-1" />{formatDate(it.doctor_approved_at)}
                    </Badge>
                  ) : (
                    <Badge style={{ background: "rgba(251,191,36,0.15)", color: "#92400e", border: "none" }}>
                      <Clock className="w-3 h-3 mr-1" />Ootab kinnitust
                    </Badge>
                  )}
                </div>
                <p className="text-xs" style={{ color: "#6e7881" }}>Esitatud: {formatDate(it.created_at)}</p>
              </div>
              <Button variant="outline" size="sm" className="rounded-full"><Eye className="w-4 h-4 mr-2" />Vaata</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
